import React, { useState } from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { AiOutlineFileSync } from "react-icons/ai";
import { toast } from "react-toastify";
import { getCompanyInfo, submitUkgConfig } from "actions/employerActions";
import { employerConstants } from "actions/types";

import Accordian from "./Accordian";
import {
  getPayrollIntegrationStatus,
  hasAcknowledgedRecurringDebits,
  hasAddedGroups,
  hasEnabledUkgPayrollIntegration,
  hasSuccessfullyConnectedPayroll,
} from "store/selectors/employer";
import { createErrorSelector } from "store/selectors";
import PromisePoller from "components/PromisePoller";
import { get, includes } from "lodash";
import {
  ConnectionInitialized,
  InitialRosterSyncError,
  PendingAdminApproval,
  PendingDebitAuthorizationAcknowledgment,
  PendingInitialRosterSync,
} from "statics/payrollIntegrationStates";
import PayrollIntegratedACHDisclaimer from "./PayrollIntegratedACHDisclaimer";
import UkgConfigForm from "./UkgConfigForm";
import type { RootState } from "types/state";

const ukgErrorSelector = createErrorSelector(
  employerConstants.SUBMIT_UKG_CONFIG
);

const mapStateToProps = (state: RootState) => {
  return {
    hasAddedGroups: hasAddedGroups(state),
    // getPayrollIntegrationStatus/hasSuccessfullyConnectedPayroll read the
    // shallow-typed employer.payrollIntegrationStatus (unknown) — cast to the
    // string/boolean these props expect
    payrollIntegrationStatus: getPayrollIntegrationStatus(state) as string,
    hasSuccessfullyConnectedPayroll: hasSuccessfullyConnectedPayroll(
      state
    ) as boolean,
    hasUkgPayrollIntegration: hasEnabledUkgPayrollIntegration(state),
    hasAcknowledgedRecurringDebits: hasAcknowledgedRecurringDebits(state),
    ukgError: ukgErrorSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  getCompanyInfo,
  submitUkgConfig,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface UkgSubmitValues {
  clientId: string;
  secret: string;
  companyCode: string;
  // (sic) — destructured by handleUkgSubmit but UkgConfigForm never supplies
  // it, so it is always undefined at runtime; preserved as-is.
  ukgCompanyId?: string;
}

const PayrollConnect = (props: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUkgSubmit = async ({
    clientId,
    secret,
    companyCode,
    ukgCompanyId,
  }: UkgSubmitValues) => {
    setIsSubmitting(true);

    const result = await props.submitUkgConfig(
      props.client as ApolloClient<object>,
      {
        clientId,
        secret,
        companyCode,
        // @ts-expect-error ukgCompanyId is not in SubmitUkgConfigMutationVariables;
        // the form never supplies it so it is always undefined here. Passed inertly
        // and preserved faithfully — pre-existing dead field.
        ukgCompanyId,
      }
    );

    if (result.success) {
      toast.success("UKG credentials submitted successfully.");
      void props.getCompanyInfo(props.client as ApolloClient<object>);
    }

    setIsSubmitting(false);
  };

  const acknowledgesInPayrollIntegration =
    !props.hasAcknowledgedRecurringDebits;

  const onboardingSteps = [
    {
      index: 1,
      label: props.hasUkgPayrollIntegration
        ? "Enter your UKG OAuth credentials"
        : "Waiting to connect with your Payroll provider.",
      content: props.hasUkgPayrollIntegration ? (
        <UkgConfigForm
          onSubmit={handleUkgSubmit}
          isSubmitting={isSubmitting}
          error={props.ukgError}
        />
      ) : null,
    },
    {
      index: 2,
      label: "Waiting for first payroll roster sync",
      isAsync: true,
    },
    {
      index: 3,
      label: "Awaiting manual payroll sync verification by customer success",
    },
    ...(acknowledgesInPayrollIntegration
      ? [
          {
            index: 4,
            label: "Acknowledgment of recurring debits",
            content: <PayrollIntegratedACHDisclaimer />,
          },
        ]
      : []),
  ];

  let activeStepNumber = 1;
  const onClick = null;
  const pendingSync = includes(
    [ConnectionInitialized, PendingInitialRosterSync, InitialRosterSyncError],
    props.payrollIntegrationStatus
  );

  if (pendingSync) {
    activeStepNumber = 2;
  }

  const pendingApproval =
    props.payrollIntegrationStatus === PendingAdminApproval;

  if (pendingApproval) {
    activeStepNumber = 3;
  }

  if (
    props.payrollIntegrationStatus === PendingDebitAuthorizationAcknowledgment
  ) {
    activeStepNumber = 4;
  }

  const shouldPoll = get(
    onboardingSteps[activeStepNumber - 1],
    "isAsync",
    false
  );

  return (
    <>
      <Accordian
        // @ts-expect-error Accordian's onClick type omits the null the component
        // accepts and guards at runtime (disabled ? noop : onClick); preserved
        // faithfully — resolves when Accordian's prop widens.
        onClick={onClick}
        icon={<AiOutlineFileSync color="#60A4BF" stroke="#60A4BF" size={16} />}
        title={"Payroll Integration"}
        description={"Integrating with your payroll provider"}
        // @ts-expect-error Accordian's actionText is typed string; null is passed
        // and rendered inertly. Preserved faithfully — resolves when the prop widens.
        actionText={null}
        disabled={!props.hasAddedGroups}
        completedAction={props.hasSuccessfullyConnectedPayroll}
        steps={onboardingSteps}
        activeStepNumber={activeStepNumber}
      />
      {shouldPoll && !props.hasSuccessfullyConnectedPayroll && (
        <PromisePoller
          interval={5000}
          promiseToPoll={() => {
            return props.getCompanyInfo(props.client as ApolloClient<object>);
          }}
          minutesToPollFor={5}
        />
      )}
    </>
  );
};

export default connector(withApollo<Props>(PayrollConnect));
